import { Component, forwardRef, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-input-file-component',
  standalone: false,
  templateUrl: './input-file-component.html',
  styleUrl: './input-file-component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFileComponent),
      multi: true,
    },
  ],
})
export class InputFileComponent implements ControlValueAccessor {
  @Input() accept = 'application/pdf';
  @Input() label = 'Upload file';
  @Input() allowMultiple = false;
  files: File[] = [];
  file: File | null = null;
  disabled = false;
  isDragging = false;
  validationError = '';

  private onChange!: (value: File | null | File[]) => void;
  private onTouched!: () => void;

  writeValue(value: File | null): void {
    this.file = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  triggerFileInput(input: HTMLInputElement) {
    if (!this.disabled) {
      input.click();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const selectedFiles = this.allowMultiple ? Array.from(input.files) : [input.files[0]];
    if (!this.areFilesValid(selectedFiles)) {
      this.files = [];
      this.onChange(this.allowMultiple ? [] : null);
      return;
    }

    this.validationError = '';
    this.files = selectedFiles;
    this.onChange(this.allowMultiple ? this.files : this.files[0] || null);
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  @HostListener('dragleave')
  onDragLeave() {
    this.isDragging = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const droppedFiles = this.allowMultiple ? Array.from(files) : [files[0]];
    if (!this.areFilesValid(droppedFiles)) {
      this.files = [];
      this.onChange(this.allowMultiple ? [] : null);
      return;
    }

    this.validationError = '';
    this.files = droppedFiles;
    this.onChange(this.allowMultiple ? this.files : this.files[0] || null);
  }

  removeFile(file: File): void {
    this.files = this.files.filter((f) => f !== file);
    if (!this.files.length) {
      this.validationError = '';
    }
    this.onChange(this.allowMultiple ? this.files : this.files[0] || null);
  }

  private areFilesValid(files: File[]): boolean {
    const acceptTypes = (this.accept || '')
      .split(',')
      .map((type) => type.trim().toLowerCase())
      .filter(Boolean);

    if (!acceptTypes.length) {
      this.validationError = '';
      return true;
    }

    const hasInvalidFile = files.some((file) => {
      const fileType = (file.type || '').toLowerCase();
      const extension = '.' + (file.name.split('.').pop() || '').toLowerCase();

      return !acceptTypes.some((acceptedType) => {
        if (acceptedType.endsWith('/*')) {
          const baseType = acceptedType.split('/')[0];
          return fileType.startsWith(baseType + '/');
        }
        if (acceptedType.startsWith('.')) {
          return extension === acceptedType;
        }
        return fileType === acceptedType;
      });
    });

    this.validationError = hasInvalidFile ? this.getFileTypeErrorMessage() : '';

    return !hasInvalidFile;
  }

  private getFileTypeErrorMessage(): string {
    const acceptTypes = (this.accept || '')
      .split(',')
      .map((type) => type.trim())
      .filter(Boolean);

    if (!acceptTypes.length) {
      return '';
    }

    const fileDescriptions = acceptTypes.map((type) => {
      const mimeToLabel: Record<string, string> = {
        'application/pdf': 'PDF',
        'image/jpeg': 'JPEG',
        'image/jpg': 'JPEG',
        'image/png': 'PNG',
        'image/gif': 'GIF',
        'image/webp': 'WebP',
        'application/msword': 'Word document',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word document',
        'application/vnd.ms-excel': 'Excel file',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel file',
        'text/csv': 'CSV file',
        'text/plain': 'text file',
      };

      if (mimeToLabel[type]) {
        return mimeToLabel[type];
      }
      if (type.endsWith('/*')) {
        const category = type.split('/')[0];
        return `${category} files`;
      }
      if (type.startsWith('.')) {
        return `${type.substring(1).toUpperCase()} files`;
      }
      return type;
    });

    if (fileDescriptions.length === 1) {
      return `Only ${fileDescriptions[0]} are allowed.`;
    }

    const lastItem = fileDescriptions.pop();
    return `Only ${fileDescriptions.join(', ')} and ${lastItem} are allowed.`;
  }

  private setFile(file: File | null) {
    this.file = file;
    this.onChange(file || null);
    this.onTouched();
  }
}
